/* ============================================================
   DRAFTS — the editor's working copy and the draft→publish flow.

   Model (plan.md §6):
     content/{locale}  published tree the public site reads
     drafts/{locale}   editable mirror (this hook)
     meta/{section}    { status: Published|Draft, edited }

   Edits update local draft state immediately (instant preview) and
   are written to drafts/{locale} debounced. Publish promotes a
   section's draft subtree into content/{locale}; Discard reverts the
   section's draft back to the published copy.
   ============================================================ */
import { useEffect, useRef, useState } from 'react'
import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../lib/firestore.js'
import { setPath, deepClone } from '../content/schema.js'
import { mergeDeep } from '../content/loader.js'
import { CONTENT } from '../content/site.js'

const WRITE_DEBOUNCE = 600

export function useDrafts(locale, published, sections) {
  // Local working copy of the draft tree. Seeded once from the draft
  // doc (or the published content) and then owned locally while editing.
  const [draft, setDraft] = useState(null)
  const [meta, setMeta] = useState({})
  const [saving, setSaving] = useState(false)
  const timer = useRef(null)
  const draftRef = useRef(null)
  draftRef.current = draft

  // Seed the local draft once: prefer an existing draft doc, else the
  // published content, else the in-code seed.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      let base = published || CONTENT
      try {
        const snap = await getDoc(doc(db, 'drafts', locale))
        if (snap.exists()) base = mergeDeep(base, snap.data())
      } catch (e) {
        console.warn('[drafts] read failed; starting from published.', e)
      }
      if (!cancelled) setDraft(deepClone(base))
    })()
    return () => {
      cancelled = true
    }
    // Seed only once per locale; published updates must not clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale])

  // Live meta (status + edited) for every section. Keyed off the resolved
  // section list so newly-ordered/renamed sections are still tracked by key.
  const sectionKeys = (sections || []).map((s) => s.key).join(',')
  useEffect(() => {
    const keys = sectionKeys ? sectionKeys.split(',') : []
    const refs = keys.map((key) =>
      onSnapshot(doc(db, 'meta', key), (snap) => {
        setMeta((m) => ({ ...m, [key]: snap.exists() ? snap.data() : { status: 'Published' } }))
      }),
    )
    return () => refs.forEach((u) => u())
  }, [sectionKeys])

  const flush = (next) => {
    setSaving(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        await setDoc(doc(db, 'drafts', locale), next)
      } catch (e) {
        console.warn('[drafts] write failed.', e)
      } finally {
        setSaving(false)
      }
    }, WRITE_DEBOUNCE)
  }

  const touchMeta = (sectionKey, status) =>
    setDoc(doc(db, 'meta', sectionKey), { status, edited: serverTimestamp() }, { merge: true })

  // Deep-equal by value (the trees are plain JSON: strings, arrays, objects).
  const sameSubtree = (a, b) => JSON.stringify(a) === JSON.stringify(b)

  // Edit a value by dotted path; updates preview now, persists debounced, and
  // sets the section's status from a real draft-vs-published comparison — so
  // typing a value back to its published state clears the Draft flag instead
  // of leaving it stuck.
  const setDraftPath = (path, value) => {
    const next = setPath(draftRef.current, path, value)
    setDraft(next)
    flush(next)
    const sectionKey = path.split('.')[0]
    const base = published || CONTENT
    const desired = sameSubtree(next[sectionKey], base[sectionKey]) ? 'Published' : 'Draft'
    const currentStatus = meta[sectionKey]?.status || 'Published'
    if (desired !== currentStatus) touchMeta(sectionKey, desired)
  }

  // Promote a section's draft into the published content doc.
  const publish = async (sectionKey) => {
    const subtree = draftRef.current[sectionKey]
    await setDoc(doc(db, 'content', locale), { [sectionKey]: subtree }, { merge: true })
    await touchMeta(sectionKey, 'Published')
  }

  // Revert a section's draft back to the published copy.
  const discard = (sectionKey) => {
    const base = published || CONTENT
    const next = setPath(draftRef.current, sectionKey, deepClone(base[sectionKey]))
    setDraft(next)
    flush(next)
    touchMeta(sectionKey, 'Published')
  }

  return { draft, meta, saving, setDraftPath, publish, discard }
}
